// Jurisdiction → display tint map. PRESENTATION ONLY (plan §2.7): the
// jurisdiction *value* is BE-owned (`jurisdiction_primary` on the wire); the
// *tint + flag* are per-client rendering — the FE itself holds the same map
// in `lib/constants.ts::COUNTRY_COLORS`, not a BE response. This computes no
// jurisdiction knowledge and branches no legal behaviour, so it is NOT a
// BE-owns-the-brain leak (same class as F-SOURCES' `flagOf()`).
//
// SYNC: colours sourced from theplaidoyer-frontend/src/lib/constants.ts
//       ::COUNTRY_COLORS (2026-05-29). See SYNC.md.
//
// Colours follow CLAUDE.md §7: fr blue, ma green, the 6 Gulf states amber,
// gb/us navy. Keys are lowercase per-country ISO. The label is resolved via
// i18n (`case.juris.*`) — never a hardcoded country name here. EU was dropped
// with the rest of the codebase ("EU c'était mon erreur") — no wire emits it.

/** Display attributes for one jurisdiction pill. */
export interface JurisdictionDisplay {
  /** Hex accent colour (CLAUDE.md §7). */
  color: string
  /** Emoji flag glyph. */
  flag: string
  /** i18n key under the `case` namespace (`case.juris.<code>`). */
  labelKey: string
}

/** Neutral fallback for an unknown / unmapped code — grey, no flag, generic
 *  label. Never throws (plan §2.7). */
export const NEUTRAL_JURISDICTION: JurisdictionDisplay = {
  color: '#6B7280',
  flag: '🏛️',
  labelKey: 'case.juris.unknown',
}

/** Frozen presentation map keyed by lowercase ISO code. */
export const JURISDICTION_DISPLAY: Record<string, JurisdictionDisplay> = {
  fr: { color: '#185FA5', flag: '🇫🇷', labelKey: 'case.juris.fr' },
  ma: { color: '#1D9E75', flag: '🇲🇦', labelKey: 'case.juris.ma' },
  // 6 Gulf states — all amber (CLAUDE.md §7).
  ae: { color: '#BA7517', flag: '🇦🇪', labelKey: 'case.juris.ae' },
  sa: { color: '#BA7517', flag: '🇸🇦', labelKey: 'case.juris.sa' },
  qa: { color: '#BA7517', flag: '🇶🇦', labelKey: 'case.juris.qa' },
  kw: { color: '#BA7517', flag: '🇰🇼', labelKey: 'case.juris.kw' },
  bh: { color: '#BA7517', flag: '🇧🇭', labelKey: 'case.juris.bh' },
  om: { color: '#BA7517', flag: '🇴🇲', labelKey: 'case.juris.om' },
  // gb (was uk) + us — navy.
  gb: { color: '#1A3C6E', flag: '🇬🇧', labelKey: 'case.juris.gb' },
  us: { color: '#1B3A6B', flag: '🇺🇸', labelKey: 'case.juris.us' },
}

/** Resolve a wire jurisdiction code (case-insensitive) to its display
 *  attributes. Unknown / empty → neutral grey fallback (no throw). */
export function jurisdictionDisplay(code: string): JurisdictionDisplay {
  return JURISDICTION_DISPLAY[code.toLowerCase()] ?? NEUTRAL_JURISDICTION
}
